import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useSelector } from 'react-redux';
import Client_ from '../../api/Client';
import { useNotification } from '../../stores/actions/StoreActions';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

type FeedbackDialogProps = { open: boolean; onOpenChange: (open: boolean) => void };

export default function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addNotificationError, addNotificationSuccess } = useNotification();
  const user = useSelector((state: any) => state.user);

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!feedback) return;
    const feedbackWithUrl = `${feedback}\n\nURL: ${window.location.href}`;

    setIsSubmitting(true);
    Client_.client_()
      .leaveFeedback(feedbackWithUrl, email)
      .then(({ err, res }) => {
        setIsSubmitting(false);
        if (err || !res?.success) {
          addNotificationError(err || 'Your report did not send. Try again.');
          return;
        }
        addNotificationSuccess('Report sent. Thank you.');
        onOpenChange(false);
        setFeedback('');
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] gap-3 rounded-2xl border-border shadow-dialog sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-[1.05rem] font-semibold tracking-tight">Report an Issue</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="feedback-text">What happened?</Label>
            <Textarea
              id="feedback-text"
              placeholder="I noticed that…"
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              className="min-h-[96px] resize-none text-[0.9rem]"
            />
            <p className="text-xs text-muted-foreground">The report includes the address of this page.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-email">Email (optional)</Label>
            <Input
              id="feedback-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="text-[0.9rem]"
            />
          </div>
          <Button type="submit" className="h-11 w-full" disabled={isSubmitting || !feedback}>
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Sending…
              </>
            ) : (
              'Send Report'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
